import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Table,
  Segment,
  Header,
  Message,
  Form,
  Loader,
  Dimmer,
  Label,
  List,
} from "semantic-ui-react";
import axios from "../../../apis/dvdlocator";

interface AsinListing {
  listing_id: string;
  msku: string | null;
  listing_title: string | null;
}

interface ProductAsin {
  asin: string;
  listings: AsinListing[];
}

interface AddAsinToProductProps {
  productId: number;
  productTitle: string;
  onClose: (didCreate?: boolean) => void;
}

export function AddAsinToProduct({
  productId,
  productTitle,
  onClose,
}: AddAsinToProductProps) {
  const [asins, setAsins] = useState<ProductAsin[]>([]);
  const [loadingAsins, setLoadingAsins] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [asin, setAsin] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const abortController = new AbortController();

    async function fetchAsins() {
      setLoadingAsins(true);
      setLoadError(null);
      try {
        const res = await axios.get(`/product/${productId}/listings`, {
          signal: abortController.signal,
        });
        setAsins(res.data.asins ?? []);
      } catch (err: any) {
        if (!abortController.signal.aborted) {
          setLoadError(
            err.response?.data?.message ||
              "Failed to load existing ASINs. Please close and try again."
          );
        }
      } finally {
        if (!abortController.signal.aborted) setLoadingAsins(false);
      }
    }

    fetchAsins();
    return () => abortController.abort();
  }, [productId]);

  const asinTrimmed = asin.trim().toUpperCase();
  const isValidFormat = /^[A-Z0-9]{10}$/.test(asinTrimmed);
  const isDuplicate = asins.some((a) => a.asin.toUpperCase() === asinTrimmed);
  const canSubmit = isValidFormat && !isDuplicate && !isSubmitting;

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!canSubmit) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await axios.post(`/product/${productId}/listing`, { asin: asinTrimmed });
      setSuccess(true);
    } catch (err: any) {
      if (err.response?.status === 409) {
        setSubmitError("This ASIN is already linked to a product.");
      } else if (err.response?.status === 404) {
        setSubmitError("Product not found.");
      } else {
        setSubmitError(
          err.response?.data?.message || "An error occurred. Please try again."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open onClose={() => onClose()} closeOnDimmerClick={false} size="large">
      <Modal.Header>Add ASIN to Product</Modal.Header>
      <Modal.Content>
        {/* Selected Product */}
        <Header as="h4" dividing>
          Selected Product
        </Header>
        <Table definition compact>
          <Table.Body>
            <Table.Row>
              <Table.Cell width={4}>Product ID</Table.Cell>
              <Table.Cell>{productId}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Product Title</Table.Cell>
              <Table.Cell>{productTitle}</Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>

        {/* Existing ASINs */}
        <Header as="h4" dividing style={{ marginTop: "1.5em" }}>
          ASINs Linked to This Product
        </Header>
        <p style={{ color: "#666", fontSize: "0.9em" }}>
          Review currently linked ASINs before adding a new one.
        </p>

        {loadError && (
          <Message negative>
            <p>{loadError}</p>
          </Message>
        )}

        {loadingAsins ? (
          <Segment style={{ minHeight: "60px" }}>
            <Dimmer active inverted>
              <Loader size="small">Loading ASINs…</Loader>
            </Dimmer>
          </Segment>
        ) : asins.length === 0 ? (
          <Segment textAlign="center" style={{ color: "#999" }}>
            No ASINs linked yet
          </Segment>
        ) : (
          <List divided relaxed celled>
            {asins.map((asinGroup) => (
              <List.Item key={asinGroup.asin}>
                <List.Content>
                  <List.Header>
                    <Label color="blue" horizontal>
                      ASIN
                    </Label>
                    {asinGroup.asin}
                  </List.Header>
                  {asinGroup.listings.length > 0 ? (
                    <List.List style={{ marginTop: "0.5em" }}>
                      {asinGroup.listings.map((listing) => (
                        <List.Item key={listing.listing_id}>
                          <List.Content>
                            <List.Description>
                              <Label basic horizontal size="tiny">
                                MSKU
                              </Label>
                              {listing.msku ?? "—"}
                              <span style={{ marginLeft: "1em", color: "#666" }}>
                                {listing.listing_title ?? "—"}
                              </span>
                            </List.Description>
                          </List.Content>
                        </List.Item>
                      ))}
                    </List.List>
                  ) : (
                    <List.Description style={{ color: "#999", marginTop: "0.5em" }}>
                      No associated listings
                    </List.Description>
                  )}
                </List.Content>
              </List.Item>
            ))}
          </List>
        )}

        {/* Add new ASIN */}
        {!success && (
          <>
            <Header as="h4" dividing style={{ marginTop: "1.5em" }}>
              Link ASIN to Product
            </Header>
            <p style={{ color: "#666", fontSize: "0.9em" }}>
              Enter an ASIN to link it to this product.
            </p>

            {submitError && (
              <Message negative>
                <p>{submitError}</p>
              </Message>
            )}

            <Form>
              <Form.Field>
                <label>New ASIN</label>
                <Form.Input
                  placeholder="Enter ASIN"
                  value={asin}
                  onChange={(e) => {
                    setAsin(e.target.value);
                    setSubmitError(null);
                  }}
                  maxLength={10}
                  disabled={isSubmitting}
                />
                {submitted && !asinTrimmed && (
                  <Label basic color="red" pointing>
                    ASIN is required
                  </Label>
                )}
                {submitted && asinTrimmed && !isValidFormat && (
                  <Label basic color="red" pointing>
                    ASIN must be exactly 10 alphanumeric characters
                  </Label>
                )}
                {asinTrimmed && isValidFormat && isDuplicate && (
                  <Label basic color="red" pointing>
                    This ASIN is already linked to this product.
                  </Label>
                )}
              </Form.Field>
            </Form>
          </>
        )}

        {success && (
          <Message positive>
            <Message.Header>ASIN Linked Successfully!</Message.Header>
            <p>
              ASIN <strong>{asinTrimmed}</strong> has been linked to this product.
            </p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={() => onClose(success)}>
          {success ? "Close" : "Cancel"}
        </Button>
        {!success && (
          <Button
            primary
            onClick={handleSubmit}
            disabled={!canSubmit || loadingAsins}
            loading={isSubmitting}
          >
            Add ASIN
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
}
